import React, { useState, useEffect } from "react";
import GameOver from "../GameOver";

const ComputerBoard = ({ gridSize = 3, players }) => {
  // Initialize states for horizontal and vertical lines
  const initialHorizontalLines = Array(gridSize * (gridSize + 1)).fill(null);
  const initialVerticalLines = Array((gridSize + 1) * gridSize).fill(null);
  const initialBoxes = Array(gridSize * gridSize).fill(null);
  const initialScores = Array(2).fill(0); // Initialize scores for each player

  const [horizontalLines, setHorizontalLines] = useState(
    initialHorizontalLines
  );
  const [verticalLines, setVerticalLines] = useState(initialVerticalLines);
  const [boxes, setBoxes] = useState(initialBoxes);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [scores, setScores] = useState(initialScores); // State for tracking scores of all players
  const [gameOver, setGameOver] = useState(false); // State to track if the game is over
  const [winner, setWinner] = useState(null); // State to track the winner

  // const players = ["Player", "Computer"];

  useEffect(() => {
    if (players[currentPlayerIndex] === "Computer" && !gameOver) {
      handleComputerMove();
    }
  }, [currentPlayerIndex, scores]);

  useEffect(() => {
    determineWinner();
  }, [scores]);

  // Function to handle line click
  const handleLineClick = (index, type) => {
    if (gameOver || players[currentPlayerIndex] === "Computer") return;
    makeMove(index, type);
  };

  const makeMove = (index, type) => {
    const currentPlayer = players[currentPlayerIndex];
    if (type === "horizontal") {
      if (!horizontalLines[index]) {
        const newHorizontalLines = [...horizontalLines];
        newHorizontalLines[index] = currentPlayer;
        setHorizontalLines(newHorizontalLines);

        updateBoxesAndPlayer(index, "horizontal", newHorizontalLines);
      }
    } else {
      if (!verticalLines[index]) {
        const newVerticalLines = [...verticalLines];
        newVerticalLines[index] = currentPlayer;
        setVerticalLines(newVerticalLines);

        updateBoxesAndPlayer(index, "vertical", newVerticalLines);
      }
    }
  };

  // Function to check and update completed boxes
  const updateBoxesAndPlayer = (index, type, updatedLines) => {
    const newBoxes = [...boxes];
    const completedBoxes = checkCompletedBoxes(
      index,
      type,
      updatedLines,
      gridSize
    );

    let boxCompleted = false;
    let completedBoxCount = 0;

    // Color the completed boxes with the current player's color
    completedBoxes.forEach((boxIndex) => {
      if (newBoxes[boxIndex] === null) {
        newBoxes[boxIndex] = players[currentPlayerIndex];
        boxCompleted = true;
        completedBoxCount++;
      }
    });

    if (boxCompleted) {
      const newScores = [...scores];
      newScores[currentPlayerIndex] += completedBoxCount;
      setScores(newScores);
      // determineWinner();
    }

    setBoxes(newBoxes);

    // Switch player only if no box was completed
    if (!boxCompleted) {
      setCurrentPlayerIndex((currentPlayerIndex + 1) % 2);
    }

    // Check if the game is over
    checkGameOver(newBoxes);
  };

  // Function to check for completed boxes
  const checkCompletedBoxes = (index, type, lines, gridSize) => {
    const completedBoxes = [];

    if (type === "horizontal") {
      const row = Math.floor(index / gridSize);
      const col = index % gridSize;

      // Check the box below the line
      if (
        row < gridSize &&
        lines[index] &&
        verticalLines[row * (gridSize + 1) + col] &&
        verticalLines[row * (gridSize + 1) + col + 1] &&
        horizontalLines[index + gridSize]
      ) {
        completedBoxes.push(row * gridSize + col);
      }

      // Check the box above the line
      if (
        row > 0 &&
        lines[index] &&
        horizontalLines[index - gridSize] &&
        verticalLines[(row - 1) * (gridSize + 1) + col] &&
        verticalLines[(row - 1) * (gridSize + 1) + col + 1]
      ) {
        completedBoxes.push((row - 1) * gridSize + col);
      }
    }

    if (type === "vertical") {
      const row = Math.floor(index / (gridSize + 1));
      const col = index % (gridSize + 1);

      // Check the box to the right of the line
      if (
        col < gridSize &&
        lines[index] &&
        horizontalLines[row * gridSize + col] &&
        horizontalLines[(row + 1) * gridSize + col] &&
        verticalLines[index + 1]
      ) {
        completedBoxes.push(row * gridSize + col);
      }

      // Check the box to the left of the line
      if (
        col > 0 &&
        lines[index] &&
        horizontalLines[row * gridSize + (col - 1)] &&
        horizontalLines[(row + 1) * gridSize + (col - 1)] &&
        verticalLines[index - 1]
      ) {
        completedBoxes.push(row * gridSize + (col - 1));
      }
    }

    return completedBoxes;
  };

  // Function to check if the game is over and determine the winner
  const checkGameOver = (newBoxes) => {
    if (newBoxes.every((box) => box !== null)) {
      // console.log(scores)
      setGameOver(true);
      determineWinner();
    }
  };

  const determineWinner = () => {
    const maxScore = Math.max(...scores);
    const winners = scores
      .map((score, index) => (score === maxScore ? players[index] : null))
      .filter(Boolean);
    setWinner(winners);
    // console.log(scores)
  };

  const handleComputerMove = () => {
    const emptyLines = getEmptyLines();
    const move = findBestMove(emptyLines);
    const { index, type } = move;
    // console.log(index,type,move)

    setTimeout(() => {
      makeMove(index, type);
    }, 1000); // Adding a delay for a more realistic computer move
  };

  const getEmptyLines = () => {
    const emptyHorizontalLines = horizontalLines
      .map((line, index) =>
        line === null ? { index, type: "horizontal" } : null
      )
      .filter(Boolean);
    const emptyVerticalLines = verticalLines
      .map((line, index) =>
        line === null ? { index, type: "vertical" } : null
      )
      .filter(Boolean);

    return [...emptyHorizontalLines, ...emptyVerticalLines];
  };

  const findBestMove = (emptyLines) => {
     // Step 1: Check if a move can complete a box
  for (let line of emptyLines) {
    if (canCompleteBox(line.index, line.type)) {
      return line;
    }
  }
  
  const randomIndex = Math.floor(Math.random() * emptyLines.length);
  if(!leavesThreeSides(emptyLines[randomIndex].index,emptyLines[randomIndex].type)){
    return emptyLines[randomIndex];
  }
  // console.log(emptyLines[randomIndex]);
  // Step 2: Check for moves that don't leave a box with three sides filled
  for (let line of emptyLines) {
    if (!leavesThreeSides(line.index, line.type)) {
      return line;
    }
  }
  
  // Step 3: If no safe move found, pick a random line
  // const randomIndex = Math.floor(Math.random() * emptyLines.length);
  return emptyLines[randomIndex];
};

// Function to check if a move can complete a box
const canCompleteBox = (index, type) => {
  if (type === "horizontal") {
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;

    // Check the box below the line
    if (
      row < gridSize &&
      horizontalLines[index] === null &&
      verticalLines[row * (gridSize + 1) + col] !== null &&
      verticalLines[row * (gridSize + 1) + col + 1] !== null &&
      horizontalLines[index + gridSize] !== null
    ) {
      return true;
    }

    // Check the box above the line
    if (
      row > 0 &&
      horizontalLines[index] === null &&
      horizontalLines[index - gridSize] !== null &&
      verticalLines[(row - 1) * (gridSize + 1) + col] !== null &&
      verticalLines[(row - 1) * (gridSize + 1) + col + 1] !== null
    ) {
      return true;
    }
  }

  if (type === "vertical") {
    const row = Math.floor(index / (gridSize + 1));
    const col = index % (gridSize + 1);

    // Check the box to the right of the line
    if (
      col < gridSize &&
      verticalLines[index] === null &&
      horizontalLines[row * gridSize + col] !== null &&
      horizontalLines[(row + 1) * gridSize + col] !== null &&
      verticalLines[index + 1] !== null
    ) {
      return true;
    }

    // Check the box to the left of the line
    if (
      col > 0 &&
      verticalLines[index] === null &&
      horizontalLines[row * gridSize + (col - 1)] !== null &&
      horizontalLines[(row + 1) * gridSize + (col - 1)] !== null &&
      verticalLines[index - 1] !== null
    ) {
      return true;
    }
  }

  return false;
};

// Function to check if a move leaves a box with three sides filled
const leavesThreeSides = (index, type) => {
  if (type === "horizontal") {
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;

    // Check the box below the line
    if (
      row < gridSize &&
      countFilledLines(row * gridSize + col, "box") === 2
    ) {
      return true;
    }

    // Check the box above the line
    if (
      row > 0 &&
      countFilledLines((row - 1) * gridSize + col, "box") === 2
    ) {
      return true;
    }
  }

  if (type === "vertical") {
    const row = Math.floor(index / (gridSize + 1));
    const col = index % (gridSize + 1);

    // Check the box to the right of the line
    if (
      col < gridSize &&
      countFilledLines(row * gridSize + col, "box") === 2
    ) {
      return true;
    }

    // Check the box to the left of the line
    if (
      col > 0 &&
      countFilledLines(row * gridSize + (col - 1), "box") === 2
    ) {
      return true;
    }
  }

  return false;
};

// Function to count filled lines of a box
const countFilledLines = (index, type) => {
  let count = 0;

  // Get row and col from index for box
  const row = Math.floor(index / gridSize);
  const col = index % gridSize;

  // Check all four sides of the box
  if (horizontalLines[row * gridSize + col]) count++;
  if (horizontalLines[(row + 1) * gridSize + col]) count++;
  if (verticalLines[row * (gridSize + 1) + col]) count++;
  if (verticalLines[row * (gridSize + 1) + col + 1]) count++;

  return count;
};


  return (
    <div className="flex flex-col items-center">
      <div className="mb-3 px-3  border-[3px] border-theme-5">
        {players.map((player, index) => (
          <span
            key={player}
            className={`mx-3 font-serif text-lg font-semibold ${
              index === 0 ? "text-blue-600" : "text-red-500"
            } `}
          >
            {player}: {scores[index]}
          </span>
        ))}
      </div>
      <div className="mb-6 text-xl font-serif">
        <h4 className="font-semibold">
          Now Playing:{" "}
          <span
            className={` font-bold ${
              currentPlayerIndex === 0 ? "text-blue-600" : "text-red-500"
            }`}
          >
            {" "}
            {players[currentPlayerIndex]}
          </span>
        </h4>
      </div>
      <div
        className="grid relative"
        style={{
          gridTemplateColumns: `repeat(${gridSize * 2 + 1}, min-content)`,
          gridTemplateRows: `repeat(${gridSize * 2 + 1}, min-content)`,
          gap: "5px",
        }}
      >
        {Array.from({ length: gridSize + 1 }, (_, row) =>
          Array.from({ length: gridSize + 1 }, (_, col) => (
            <React.Fragment key={`${row}-${col}`}>
              {/* Dots */}
              <div
                className="w-2 h-2 bg-theme-2 rounded-full"
                style={{
                  gridColumn: col * 2 + 1,
                  gridRow: row * 2 + 1,
                }}
              ></div>

              {/* Horizontal Lines */}
              {col < gridSize && (
                <div
                  className={`h-1 w-8   cursor-pointer transition-colors duration-300 ${
                    horizontalLines[row * gridSize + col] === players[0]
                      ? "bg-blue-600 "
                      : horizontalLines[row * gridSize + col] === players[1]
                      ? "bg-red-500 "
                      : "bg-gray-300 md:hover:bg-theme-4"
                  }`}
                  onClick={() =>
                    handleLineClick(row * gridSize + col, "horizontal")
                  }
                  style={{
                    gridColumn: col * 2 + 2,
                    gridRow: row * 2 + 1,
                  }}
                ></div>
              )}

              {/* Vertical Lines */}
              {row < gridSize && (
                <div
                  className={`w-1 h-8  cursor-pointer transition-colors duration-300 ${
                    verticalLines[row * (gridSize + 1) + col] === players[0]
                      ? "bg-blue-600"
                      : verticalLines[row * (gridSize + 1) + col] === players[1]
                      ? "bg-red-500"
                      : "bg-gray-300 md:hover:bg-theme-4"
                  }`}
                  onClick={() =>
                    handleLineClick(row * (gridSize + 1) + col, "vertical")
                  }
                  style={{
                    gridColumn: col * 2 + 1,
                    gridRow: row * 2 + 2,
                  }}
                ></div>
              )}
            </React.Fragment>
          ))
        )}
        {/* Boxes */}
        {boxes.map((box, index) => (
          <div
            key={index}
            className={`absolute w-8 h-8 rounded-full ${
              box === players[0]
                ? "bg-blue-400"
                : box === players[1]
                ? "bg-red-400"
                : ""
            }`}
            style={{
              gridColumn: (index % gridSize) * 2 + 2,
              gridRow: Math.floor(index / gridSize) * 2 + 2,
            }}
          ></div>
        ))}
      </div>
      <GameOver
        gameOver={gameOver}
        winner={winner}
        scores={scores}
        players={players}
      />
    </div>
  );
};

export default ComputerBoard;
